import React, { useState, useEffect } from 'react';
import { Select, Input, Button, Upload, Steps, Row, Col, Space, ConfigProvider, Typography, App, Tooltip, Form, message, Radio } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import SectionTitle from './text_components/SectionTitle';
import { EditOutlined, InboxOutlined, InfoCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import axios from 'axios';
import { getCurrentUserId } from '../utils/userUtils';

const { Option } = Select;
const { Step } = Steps;
const { Title, Text } = Typography;
const { Dragger } = Upload;

// Reuse the styled components from Reporting.js
const StyledCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  transition: all 0.3s ease;
  border: 1px solid #bfdbfe;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
    transform: translateY(-5px);
  }
`;

const StyledInput = styled(Input)`
  border-radius: 10px;
  border: 2px solid #e6e6e6;
  transition: all 0.3s ease;

  &:hover, &:focus {
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const StyledButton = styled(Button)`
  border-radius: 10px;
  height: 40px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const StyledSteps = styled(Steps)`
  .ant-steps-item-icon {
    background: white;
    border-color: #4a90e2;
  }

  .ant-steps-item-title {
    font-weight: 600;
  }
`;

const fieldMapping = {
  "Plan Type": "plan_type",
  "Eligibility Requirements": "eligibility_requirements",
  "Automatic Enrollment": "automatic_enrollment",
  "Entry Date": "entry_date",
  "Eligible Contribution Types": "eligible_contribution_types",
  "Match": "match",
  "True Up": "true_up",
  "Loans": "loans",
  "Hardship Distributions": "hardship_distributions",
  "In Service Distributions": "in_service_distributions",
  "Vesting": "vesting",
  "QDIA": "qdia"
};

const displayNameMap = {
  plan_type: "Plan Type",
  eligibility_requirements: "Eligibility Requirements",
  automatic_enrollment: "Automatic Enrollment",
  entry_date: "Entry Date",
  eligible_contribution_types: "Eligible Contribution Types",
  match: "Match",
  true_up: "True Up",
  loans: "Loans",
  hardship_distributions: "Hardship Distributions",
  in_service_distributions: "In Service Distributions",
  vesting: "Vesting",
  qdia: "QDIA"
};

const TPAClientOnboarding = () => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const [onboardingData, setOnboardingData] = useState({
    companyName: '',
    planName: '',
    ein: '',
    contactInfo: '',
    assignedAdvisor: '',
    documents: {
      adoptionAgreement: null,
      planSummary: null,
      participantCensus: null,
      additionalDocs: []
    },
    planDesign: {
      eligibilityRequirements: '',
      vestingSchedule: '',
      contributionLimits: ''
    }
  });

  const [extractedData, setExtractedData] = useState(null);
  const [showPDFReview, setShowPDFReview] = useState(false);

  const handleChange = (key, value) => {
    setOnboardingData(prev => ({
      ...prev,
      [key]: value,
      // Reset related fields when switching input method
      ...(key === 'inputMethod' && {
        documents: {
          ...prev.documents,
          adoptionAgreement: null
        },
        planDesign: {
          ...prev.planDesign
        }
      })
    }));

    // Reset PDF review state when switching input method
    if (key === 'inputMethod') {
      setShowPDFReview(false);
      setExtractedData(null);
    }
  };

  const getTooltipText = (key) => {
    const tooltips = {
      plan_type: "Select the type of retirement plan (e.g., 401(k), 403(b), etc.)",
      eligibility_requirements: "Specify the conditions employees must meet to participate in the plan",
      automatic_enrollment: "Indicate if employees are automatically enrolled in the plan",
      entry_date: "When eligible employees can begin participating in the plan",
      eligible_contribution_types: "Types of contributions allowed (e.g., pre-tax, Roth, after-tax)",
      match: "Describe the employer matching contribution formula",
      true_up: "Details about true-up contributions if applicable",
      loans: "Loan provisions and requirements",
      hardship_distributions: "Rules and conditions for hardship withdrawals",
      in_service_distributions: "Requirements for in-service distributions",
      vesting: "Schedule for vesting of employer contributions",
      qdia: "Qualified Default Investment Alternative details"
    };
    return tooltips[key] || "Enter details";
  };

  const renderLabel = (label, tooltip) => (
    <div className="flex items-center">
      <span>{label}</span>
      <Tooltip title={tooltip}>
        <InfoCircleOutlined className="ml-2" />
      </Tooltip>
    </div>
  );

  const steps = [
    {
      title: 'Welcome and Setup',
      content: (
        <Form form={form} layout="vertical">
          <Form.Item
            label={renderLabel("Company Name", "Legal name of the company")}
            name="companyName"
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <StyledInput placeholder="Enter company name" />
          </Form.Item>
          <Form.Item
            label={renderLabel("Plan Name", "Official name of the 401k plan")}
            name="planName"
            rules={[{ required: true, message: 'Please enter plan name' }]}
          >
            <StyledInput placeholder="Enter plan name" />
          </Form.Item>
          <Form.Item
            label={renderLabel("EIN", "Employer Identification Number")}
            name="ein"
            rules={[{ required: true, message: 'Please enter EIN' }]}
          >
            <StyledInput placeholder="XX-XXXXXXX" />
          </Form.Item>
          <Form.Item
            label={renderLabel("Contact Information", "Primary contact details")}
            name="contactInfo"
            rules={[{ required: true, message: 'Please enter contact information' }]}
          >
            <Input.TextArea placeholder="Enter contact details" rows={4} />
          </Form.Item>
        </Form>
      )
    },
    {
      title: 'Document Uploads',
      content: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            {renderLabel("Plan Adoption Agreement", "Upload the signed plan adoption agreement")}
            <Dragger
              name="adoptionAgreement"
              multiple={false}
              onChange={(info) => handleFileUpload(info, 'adoptionAgreement')}
              beforeUpload={() => false}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Click or drag file to upload</p>
            </Dragger>
          </div>
          
          <div>
            {renderLabel("Plan Summary", "Upload plan summary document")}
            <Dragger
              name="planSummary"
              multiple={false}
              onChange={(info) => handleFileUpload(info, 'planSummary')}
              beforeUpload={() => false}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Click or drag file to upload</p>
            </Dragger>
          </div>

          <div>
            {renderLabel("Participant Census", "Upload initial participant information")}
            <Dragger
              name="participantCensus"
              multiple={false}
              onChange={(info) => handleFileUpload(info, 'participantCensus')}
              beforeUpload={() => false}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Click or drag file to upload</p>
            </Dragger>
          </div>
        </Space>
      )
    },
    {
      title: 'Plan Setup',
      content: (
        <div>
          <div className="mb-4">
            <Radio.Group 
              onChange={(e) => handleChange('inputMethod', e.target.value)} 
              value={onboardingData.inputMethod || 'manual'}
              className="flex space-x-4 mb-6"
            >
              <Radio value="manual" className="text-lg">Manual Entry</Radio>
              <Radio value="pdf" className="text-lg">Upload PDF</Radio>
            </Radio.Group>
          </div>

          {onboardingData.inputMethod === 'pdf' ? (
            <div className="bg-gray-50 p-6 rounded-lg border-2 border-dashed border-gray-300">
              <Tooltip title="Upload the adoption agreement PDF. The system will automatically extract key plan design information.">
                <Dragger
                  name="adoptionAgreement"
                  multiple={false}
                  fileList={onboardingData.documents.adoptionAgreement ? [onboardingData.documents.adoptionAgreement] : []}
                  onChange={(info) => handlePDFUpload(info, 'adoptionAgreement')}
                  accept=".pdf"
                  beforeUpload={() => false}
                  className="bg-white"
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined className="text-4xl text-blue-500" />
                  </p>
                  <p className="ant-upload-text text-lg font-medium mt-4">
                    Click or drag adoption agreement PDF to this area
                  </p>
                  <p className="ant-upload-hint text-sm text-gray-500">
                    The file will be processed to extract plan design information.
                  </p>
                </Dragger>
              </Tooltip>

              {onboardingData.documents.adoptionAgreement && (
                <Button
                  type="primary"
                  onClick={handleProcessPDF}
                  loading={loading}
                  className="mt-4"
                >
                  Process PDF
                </Button>
              )}
            </div>
          ) : (
            <Form form={form} layout="vertical" className="grid grid-cols-1 gap-6">
              {Object.entries(fieldMapping).map(([displayKey, formKey]) => (
                <Form.Item
                  key={formKey}
                  label={renderLabel(displayNameMap[formKey], getTooltipText(formKey))}
                  name={formKey}
                  rules={[{ required: true, message: `Please enter ${displayNameMap[formKey].toLowerCase()}` }]}
                >
                  {formKey === 'automatic_enrollment' ? (
                    <Select
                      value={onboardingData.planDesign?.[formKey]}
                      onChange={(value) => handlePlanDesignChange(formKey, value)}
                      style={{ width: '100%' }}
                    >
                      <Option value="Yes">Yes</Option>
                      <Option value="No">No</Option>
                    </Select>
                  ) : (
                    <Input.TextArea
                      rows={3}
                      value={onboardingData.planDesign?.[formKey]}
                      onChange={(e) => handlePlanDesignChange(formKey, e.target.value)}
                      placeholder={`Enter ${displayNameMap[formKey].toLowerCase()}...`}
                    />
                  )}
                </Form.Item>
              ))}
            </Form>
          )}

          {showPDFReview && (
            <div className="mt-8">
              <Title level={4}>Review Extracted Data</Title>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
                <Text strong className="text-amber-800">
                  Please review and edit the extracted information before proceeding
                </Text>
              </div>
              
              <Form layout="vertical" className="grid grid-cols-1 gap-6">
                {Object.entries(extractedData).map(([key, value]) => (
                  <Form.Item
                    key={key}
                    label={displayNameMap[fieldMapping[key]] || key}
                  >
                    <Input.TextArea
                      rows={3}
                      defaultValue={value}
                      onChange={(e) => handleExtractedDataChange(key, e.target.value)}
                    />
                  </Form.Item>
                ))}
              </Form>

              <div className="flex justify-end space-x-4 mt-4">
                <Button onClick={() => setShowPDFReview(false)}>
                  Cancel
                </Button>
                <Button 
                  type="primary"
                  onClick={handleSaveExtractedData}
                >
                  Save Extracted Data
                </Button>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Review and Submit',
      content: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Title level={4}>Review Information</Title>
          
          <div>
            <Text strong>Company Details</Text>
            <ul>
              <li>Company Name: {onboardingData.companyName}</li>
              <li>Plan Name: {onboardingData.planName}</li>
              <li>EIN: {onboardingData.ein}</li>
            </ul>
          </div>
          
          <div>
            <Text strong>Uploaded Documents</Text>
            <ul>
              <li>Adoption Agreement: {onboardingData.documents.adoptionAgreement ? '✓' : '✗'}</li>
              <li>Plan Summary: {onboardingData.documents.planSummary ? '✓' : '✗'}</li>
              <li>Participant Census: {onboardingData.documents.participantCensus ? '✓' : '✗'}</li>
            </ul>
          </div>
          
          <div>
            <Text strong>Plan Design</Text>
            <ul>
              <li>Eligibility Requirements: {onboardingData.planDesign.eligibilityRequirements}</li>
              <li>Vesting Schedule: {onboardingData.planDesign.vestingSchedule}</li>
              <li>Contribution Limits: {onboardingData.planDesign.contributionLimits}</li>
            </ul>
          </div>
        </Space>
      )
    }
  ];

  const handleFileUpload = (info, documentType) => {
    if (info.file.status !== 'uploading') {
      setOnboardingData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [documentType]: info.file
        }
      }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Submit logic here
      messageApi.success('Client onboarding completed successfully!');
    } catch (error) {
      messageApi.error('Failed to complete onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const next = () => {
    form.validateFields().then(() => {
      setCurrentStep(currentStep + 1);
    });
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const isMobile = useMediaQuery({ maxWidth: 767 });

  const handlePDFUpload = (info, documentType) => {
    if (info.file.status !== 'uploading') {
      setOnboardingData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [documentType]: info.file
        }
      }));
    }
  };

  const handleProcessPDF = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', onboardingData.documents.adoptionAgreement.originFileObj);
      formData.append('company_name', onboardingData.companyName);

      const response = await axios.post(
        'https://plansync-backend-234288438538.us-central1.run.app/api/plan-design/2024/process-pdf',
        formData,
        {
          headers: { 'user-id': getCurrentUserId() }
        }
      );

      if (response.data.extracted_data) {
        setExtractedData(response.data.extracted_data);
        setShowPDFReview(true);
        messageApi.success('PDF processed successfully. Please review the extracted data.');
      }
    } catch (error) {
      console.error('Error processing PDF:', error);
      messageApi.error('Failed to process PDF: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExtractedDataChange = (key, value) => {
    setExtractedData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveExtractedData = () => {
    const updatedPlanDesign = {};
    Object.entries(extractedData).forEach(([key, value]) => {
      const formKey = fieldMapping[key];
      if (formKey) {
        updatedPlanDesign[formKey] = value;
      }
    });

    setOnboardingData(prev => ({
      ...prev,
      planDesign: {
        ...prev.planDesign,
        ...updatedPlanDesign
      }
    }));

    setShowPDFReview(false);
    setExtractedData(null);
    messageApi.success('Extracted data saved successfully');
  };

  const handlePlanDesignChange = (key, value) => {
    setOnboardingData(prev => ({
      ...prev,
      planDesign: {
        ...prev.planDesign,
        [key]: value
      }
    }));
  };

  return (
    <App>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#4a90e2',
            borderRadius: 10,
            fontFamily: "'Inter', sans-serif",
          },
        }}
      >
        {contextHolder}
        <Row justify="center" align="middle" className="min-h-screen py-8">
          <Col xs={24} sm={24} md={22} lg={20} xl={18}>
            <StyledCard>
              <SectionTitle>TPA Client Onboarding</SectionTitle>
              
              <StyledSteps current={currentStep} onChange={setCurrentStep}>
                {steps.map(item => (
                  <Step key={item.title} title={item.title} />
                ))}
              </StyledSteps>

              <div className="mt-8">
                {steps[currentStep].content}
              </div>

              <div className="steps-action mt-8">
                <Space>
                  {currentStep > 0 && (
                    <StyledButton onClick={prev}>
                      Previous
                    </StyledButton>
                  )}
                  {currentStep < steps.length - 1 && (
                    <StyledButton type="primary" onClick={next}>
                      Next
                    </StyledButton>
                  )}
                  {currentStep === steps.length - 1 && (
                    <StyledButton
                      type="primary"
                      onClick={handleSubmit}
                      loading={loading}
                      icon={<CheckCircleOutlined />}
                    >
                      Complete Onboarding
                    </StyledButton>
                  )}
                </Space>
              </div>
            </StyledCard>
          </Col>
        </Row>
      </ConfigProvider>
    </App>
  );
};

export default TPAClientOnboarding;
